import { Check, Clock3, PhoneOff, Trophy, UserX, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";

import type { AcqFeedbackCode } from "../../api/acqService";
import { FEEDBACK_LABEL } from "../../model/acqLabels";

/**
 * 五个反馈按钮(契约 §4.2)。
 *
 * ★ 这是整个产品**唯一要求用户输入的东西** —— 所以它不做成下拉框、不弹表单、不要二次确认:
 *   跟完顺手一点就完事。下拉要两次点击且第一次点完看不见选项,弹窗要三次点击,
 *   两种都会让"随手点一下"变成"回头再说",而回头就是永远不点(反馈不回流 ⇒ 权重不动 ⇒
 *   明天的单子还是一样准)。
 * ★ 选中态是**填色 + 勾**,不是只换个边框:小屏上只换边框肉眼分不出点没点。
 */
const ITEMS: {
  code: AcqFeedbackCode;
  icon: LucideIcon;
  /** 选中后的填色(皮肤 token,三套皮肤都成立)。 */
  on: string;
  /** 未选中时的 hover 提示色。 */
  hover: string;
}[] = [
  {
    code: "won",
    icon: Trophy,
    on: "border-success bg-success text-success-foreground",
    hover: "hover:border-success/60 hover:text-success",
  },
  {
    code: "lost",
    icon: X,
    on: "border-muted-foreground bg-muted-foreground text-background",
    hover: "hover:border-muted-foreground/60",
  },
  {
    code: "wrong_person",
    icon: UserX,
    on: "border-warning bg-warning text-warning-foreground",
    hover: "hover:border-warning/60 hover:text-warning",
  },
  {
    code: "bad_phone",
    icon: PhoneOff,
    on: "border-destructive bg-destructive text-destructive-foreground",
    hover: "hover:border-destructive/60 hover:text-destructive",
  },
  {
    code: "follow_up",
    icon: Clock3,
    on: "border-info bg-info text-info-foreground",
    hover: "hover:border-info/60 hover:text-info",
  },
];

export function FeedbackBar({
  value,
  onPick,
  busy,
  className,
}: {
  value?: AcqFeedbackCode | null;
  onPick: (code: AcqFeedbackCode) => void;
  busy?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-2xs text-muted-foreground">
        {value
          ? `已标记：${FEEDBACK_LABEL[value]}（可改）`
          : "跟完之后顺手点一下，明天的单子会更准"}
      </span>
      {/* ★ flex-wrap 不是 grid-cols-5:390px 窄屏下五个中文按钮一行放不下,
          grid 会把每个挤成两行竖排字(本仓 0916 踩过一次),wrap 是自然掉行。 */}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="线索反馈">
        {ITEMS.map(({ code, icon: Icon, on, hover }) => {
          const active = value === code;
          return (
            <button
              key={code}
              type="button"
              disabled={busy}
              aria-pressed={active}
              onClick={() => onPick(code)}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs font-semibold",
                "transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                active ? on : cn("border-border bg-background text-muted-foreground", hover)
              )}
            >
              {active ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              {FEEDBACK_LABEL[code]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
